import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Clock, 
  Calendar, 
  Users, 
  FileText, 
  Printer,
  Download,
  RefreshCw,
  Timer,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const AttendanceReportPage = () => {
  const { API_URL, getAuthHeader, user } = useAuth();
  const { t } = useLanguage();
  
  const [staffUsers, setStaffUsers] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const months = [
    { value: 1, label: 'يناير' },
    { value: 2, label: 'فبراير' },
    { value: 3, label: 'مارس' },
    { value: 4, label: 'أبريل' },
    { value: 5, label: 'مايو' },
    { value: 6, label: 'يونيو' },
    { value: 7, label: 'يوليو' },
    { value: 8, label: 'أغسطس' },
    { value: 9, label: 'سبتمبر' },
    { value: 10, label: 'أكتوبر' },
    { value: 11, label: 'نوفمبر' },
    { value: 12, label: 'ديسمبر' },
  ];

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, attendanceRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff-users`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/attendance?month=${selectedMonth}&year=${selectedYear}`, { headers: getAuthHeader() })
      ]);
      setStaffUsers(staffRes.data || []);
      setAttendanceRecords(attendanceRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total hours for a staff member in the selected month
  const calculateTotalHours = (staffId) => {
    const records = attendanceRecords.filter(r => r.staff_id === staffId);
    let totalMinutes = 0;
    
    records.forEach(record => {
      if (record.clock_in && record.clock_out) {
        const clockIn = new Date(record.clock_in);
        const clockOut = new Date(record.clock_out);
        totalMinutes += (clockOut - clockIn) / (1000 * 60);
      }
    });
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return { hours, minutes, totalMinutes };
  };

  // Get attendance records for a staff member
  const getStaffRecords = (staffId) => {
    return attendanceRecords.filter(r => r.staff_id === staffId);
  };

  // Print report
  const handlePrint = () => {
    const printContent = document.getElementById('attendance-report');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html dir="rtl">
        <head>
          <title>تقرير الدوام الشهري - ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
            th { background-color: #f5f5f5; }
            h1 { text-align: center; color: #333; }
            .header { text-align: center; margin-bottom: 30px; }
            .total { font-weight: bold; background-color: #fef3c7; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>تقرير الدوام الشهري</h1>
            <p>${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}</p>
          </div>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-800">تقرير الدوام الشهري</h1>
            <p className="text-stone-500 text-sm">عرض ساعات عمل الموظفين</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            تحديث
          </Button>
          <Button onClick={handlePrint} variant="outline" size="sm" className="gap-2">
            <Printer className="w-4 h-4" />
            طباعة
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label>الشهر:</Label>
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(month => (
                    <SelectItem key={month.value} value={String(month.value)}>{month.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>السنة:</Label>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{staffUsers.length}</p>
            <p className="text-sm opacity-90">عدد الموظفين</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4 text-center">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{attendanceRecords.length}</p>
            <p className="text-sm opacity-90">سجلات الحضور</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4 text-center">
            <Timer className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">
              {Math.round(staffUsers.reduce((acc, s) => acc + calculateTotalHours(s.id).totalMinutes, 0) / 60)}
            </p>
            <p className="text-sm opacity-90">إجمالي الساعات</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">
              {staffUsers.length > 0 ? Math.round(attendanceRecords.length / staffUsers.length) : 0}
            </p>
            <p className="text-sm opacity-90">متوسط أيام العمل</p>
          </CardContent>
        </Card>
      </div>

      {/* Report Table */}
      <div id="attendance-report">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              تقرير {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {staffUsers.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">لا يوجد موظفين</p>
                <p className="text-sm mt-2">أضف موظفين من صفحة إدارة المستخدمين</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">#</TableHead>
                      <TableHead className="text-right">اسم الموظف</TableHead>
                      <TableHead className="text-center">أيام العمل</TableHead>
                      <TableHead className="text-center">إجمالي الساعات</TableHead>
                      <TableHead className="text-center">متوسط يومي</TableHead>
                      <TableHead className="text-center">الحالة</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffUsers.map((staff, index) => {
                      const { hours, minutes, totalMinutes } = calculateTotalHours(staff.id);
                      const records = getStaffRecords(staff.id);
                      const avgHours = records.length > 0 ? (totalMinutes / records.length / 60).toFixed(1) : 0;
                      
                      return (
                        <TableRow key={staff.id}>
                          <TableCell className="font-bold">{index + 1}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{staff.name}</p>
                              <p className="text-xs text-stone-500">@{staff.username}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm">
                              {records.length} يوم
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-bold text-lg">{hours}</span>
                            <span className="text-stone-500 text-sm">:{String(minutes).padStart(2, '0')}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-amber-600">{avgHours} ساعة</span>
                          </TableCell>
                          <TableCell className="text-center">
                            {staff.is_active ? (
                              <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                <CheckCircle className="w-4 h-4" />
                                نشط
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-stone-400 text-sm">
                                <AlertCircle className="w-4 h-4" />
                                معطل
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Total Row */}
                    <TableRow className="bg-amber-50 font-bold">
                      <TableCell colSpan={2} className="text-left">الإجمالي</TableCell>
                      <TableCell className="text-center">
                        {attendanceRecords.length} سجل
                      </TableCell>
                      <TableCell className="text-center">
                        {Math.floor(staffUsers.reduce((acc, s) => acc + calculateTotalHours(s.id).totalMinutes, 0) / 60)} ساعة
                      </TableCell>
                      <TableCell className="text-center">-</TableCell>
                      <TableCell className="text-center">-</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AttendanceReportPage;
